import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { useToast } from "@/hooks/use-toast"
import { auth } from "@/lib/firebase"

const deleteCase = async (caseId: string): Promise<void> => {
	// Remove any trailing slash from the caseId if present
	const normalizedCaseId = caseId.replace(/\/$/, "")

	const user = auth.currentUser
	if (!user) {
		throw new Error("User not found.")
	}
	const token = await user.getIdToken()
	const response = await fetch(
		`${import.meta.env.VITE_API_URL}/api/cases/${normalizedCaseId}/`,
		{
			method: "DELETE",
			// Add explicit headers to prevent browser from modifying the request
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
			},
		}
	)

	if (!response.ok) {
		const errorText = await response.text()
		throw new Error(`Failed to delete case: ${response.status} ${errorText}`)
	}
}

export const useDeleteCase = (caseId: string) => {
	const queryClient = useQueryClient()
	const navigate = useNavigate()
	const { toast } = useToast()

	return useMutation<void, Error, string>({
		mutationFn: deleteCase,
		onSuccess: () => {
			// Invalidate the cases list query
			queryClient.invalidateQueries({ queryKey: ["cases"] })

			// Navigate back to cases list
			navigate("/cases")

			toast({
				title: "Success",
				description: "Case and all associated documents have been deleted.",
			})
		},
		onError: (error) => {
			toast({
				title: "Error",
				description: error.message || "Failed to delete case.",
				variant: "destructive",
			})
		},
	})
}
